import { useState } from 'react';
import { Alert, Avatar } from '@mui/material';
import { LoadError } from '../LoadError';
import { ScoringCategory, ScoringInvitee, ScoringRequestRow } from '../../../../types/advise';
import { DEFAULT_ADVANCE_BUTTON_STYLE, DEFAULT_CANCEL_BUTTON_STYLE } from '../../../../utils/styleHelpers';
import { TextBlock, TextSpan } from '../../../Atoms/Text';
import { TextButton } from '../../../Atoms/TextButton';
import { ConfirmDialog } from '../ConfirmDialog';
import {
  emptyInvitee,
  fullName,
  initials,
  InviteeDraft,
  isReadyInvitee,
  isUnanswered,
  resendable,
  scopeLabel,
  STATUS_TONES,
  statusLabel,
  toScoringInvitee
} from './RequestSupplierScoring.calc';
import { ScoringInviteeFields } from './ScoringInviteeFields';

const PILL_STYLES = {
  neutral: { color: 'grey6', background: 'bg-grey-1' },
  amber: { color: 'amber', background: 'bg-amber-50' },
  green: { color: 'green600', background: 'bg-green-50' },
  // The app's `red` is a single colour, so `bg-red-50` never generates.
  red: { color: 'red', background: 'bg-[#FDECEC]' }
} as const;

interface ScoringRequestsSentProps {
  // Null while the list loads.
  requests: ScoringRequestRow[] | null;
  failed: boolean;
  onRetry: () => void;
  categories: ScoringCategory[];
  // Resolves to why the reassignment was refused, or null once it saved.
  onReassign: (request: ScoringRequestRow, invitee: ScoringInvitee) => Promise<string | null>;
  // Invites the same person again after their email failed; the dialog says how it went.
  onResend: (request: ScoringRequestRow) => Promise<void>;
}

/** Every request sent for the supplier, newest first, with Reassign on the ones still unanswered and Retry on a failed email. */
export const ScoringRequestsSent = ({
  requests,
  failed,
  onRetry,
  categories,
  onReassign,
  onResend
}: ScoringRequestsSentProps) => {
  const [reassigning, setReassigning] = useState<{ request: ScoringRequestRow; invitee: InviteeDraft } | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [resending, setResending] = useState<string | null>(null);
  const retryable = resendable(requests ?? []);

  const resend = async (request: ScoringRequestRow) => {
    setResending(request.id);
    try {
      await onResend(request);
    } finally {
      setResending(null);
    }
  };

  const startReassign = (request: ScoringRequestRow) => {
    setRefusal(null);
    setReassigning({
      request,
      invitee: emptyInvitee(request.all_categories ? [] : request.scope.map((category) => category.key))
    });
  };

  const confirmReassign = async () => {
    if (!reassigning) return;
    const why = await onReassign(reassigning.request, toScoringInvitee(reassigning.invitee));
    setConfirming(false);
    if (why) {
      setRefusal(why);
      return;
    }
    setReassigning(null);
  };

  return (
    <div className="flex flex-col gap-3">
      <TextBlock
        size="xs"
        weight="semibold"
        color="grey6">
        SCORING REQUESTS SENT
      </TextBlock>
      {failed && (
        <LoadError
          subject="the requests already sent"
          consequence="Send Requests waits until they load"
          onRetry={onRetry}
        />
      )}
      {requests?.length === 0 && (
        <TextBlock
          size="sm"
          color="grey6">
          No scoring requests have been sent for this supplier yet.
        </TextBlock>
      )}
      {requests?.map((request) => {
        // A status this page doesn't know yet reads as neutral rather than breaking the page.
        const pill = PILL_STYLES[STATUS_TONES[request.status] ?? 'neutral'];
        const open = reassigning?.request.id === request.id;
        return (
          <div
            key={request.id}
            className="flex flex-col gap-2 border-b border-grey-2 pb-3 last:border-b-0">
            <div className="flex items-start gap-3">
              <Avatar sx={{ width: 32, height: 32, fontSize: 13, bgcolor: 'primary.dark' }}>{initials(request)}</Avatar>
              <div className="flex min-w-0 flex-1 flex-col">
                <TextSpan weight="semibold">
                  {fullName(request)}
                  {request.role && ` — ${request.role}`}
                </TextSpan>
                <TextSpan
                  size="xs"
                  color="grey6">
                  {request.email} · {scopeLabel(request)}
                </TextSpan>
                {isUnanswered(request) && request.link_lapsed && (
                  <TextSpan
                    size="xs"
                    color="amber">
                    Their link has lapsed. Invite them again to send a new one.
                  </TextSpan>
                )}
              </div>
              <div className="flex flex-col items-end gap-1">
                <TextSpan
                  size="xs"
                  weight="medium"
                  color={pill.color}
                  className={`whitespace-nowrap rounded-full px-2.5 py-1 ${pill.background}`}>
                  {statusLabel(request)}
                </TextSpan>
                {isUnanswered(request) && !open && (
                  <TextButton
                    size="xs"
                    onClick={() => startReassign(request)}>
                    Reassign
                  </TextButton>
                )}
                {retryable.has(request.id) && (
                  <TextButton
                    size="xs"
                    disabled={resending !== null}
                    onClick={() => resend(request)}>
                    Retry
                  </TextButton>
                )}
              </div>
            </div>
            {open && reassigning && (
              <div className="flex flex-col gap-2 pl-11">
                <TextSpan
                  size="sm"
                  color="grey6">
                  Reassign to
                </TextSpan>
                <ScoringInviteeFields
                  invitee={reassigning.invitee}
                  categories={categories}
                  disabled={confirming}
                  onChange={(patch) => {
                    setRefusal(null);
                    setReassigning({ ...reassigning, invitee: { ...reassigning.invitee, ...patch } });
                  }}
                />
                {refusal && <Alert severity="error">{refusal}</Alert>}
                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    className={DEFAULT_CANCEL_BUTTON_STYLE}
                    onClick={() => setReassigning(null)}>
                    Cancel
                  </button>
                  <button
                    type="button"
                    className={DEFAULT_ADVANCE_BUTTON_STYLE}
                    disabled={!isReadyInvitee(reassigning.invitee)}
                    onClick={() => setConfirming(true)}>
                    Reassign
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      })}
      <TextBlock
        size="xs"
        color="grey6">
        All scoring-request activity is logged for compliance.
      </TextBlock>
      {reassigning && (
        <ConfirmDialog
          open={confirming}
          title={`Reassign to ${reassigning.invitee.firstName.trim()} ${reassigning.invitee.lastName.trim()}?`}
          message={
            `${fullName(reassigning.request)}'s link will stop working, and we'll email them that the request was ` +
            `reassigned. ${reassigning.invitee.firstName.trim()} will be emailed a new link.`
          }
          confirmLabel="Reassign"
          confirmColor="primary"
          onConfirm={confirmReassign}
          onCancel={() => setConfirming(false)}
        />
      )}
    </div>
  );
};
